import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
	actions,
	listUsers,
	readOtherUser,
	updatePassword,
	updateUser,
} from "./slice";
import FormField from "../../components/FormField";
import LoadingButton from "../../components/LoadingButton";
import CustomAutoComplete from "../../components/CustomAutoComplete";
import {
	SET_CURRENT_USER_PROFILE,
	UPDATE_LOGGED_USER_ATTRIBUTES,
} from "../../app/actionTypes";
import { actions as commonActions } from "../../app/commonSlice";
import { maskCpfCNPJ, maskPhone, unmaskNumber } from "../../libs/masks";
import Alert from "../../components/Alert";
import { validation } from "./validation";

function Profile() {
	const appDispatch = useAppDispatch();
	const { currentUser, updateLoading, updatePasswordLoading } = useAppSelector(
		(state: RootState) => state.profileReducer,
	);
	const { user, groups }: any = useAppSelector(
		(state: RootState) => state.commonReducer,
	);
	const isAdmin = !!groups.find(
		(group: any) => group.name === "Administradores",
	);
	useEffect(() => {
		if (!currentUser) {
			appDispatch(actions[SET_CURRENT_USER_PROFILE](user));
		}
	}, []);

	const [formData, setFormData] = useState<any>(user || {});
	const [formDataPassword, setFormDataPassword] = useState<any>({
		currentPassword: "",
		newPassword: "",
	});
	const [showErrors, setShowErrors] = useState<boolean>(false);

	const [errorUpdateUser, setErrorUpdateUser] = useState("");
	const [errorUpdatePassword, setErrorUpdatePassword] = useState("");
	const [successUpdateUser, setSuccessUpdateUser] = useState("");
	const [successUpdatePassword, setSuccessUpdatePassword] = useState("");

	const handleOnSubmitUpdateAttributes: React.FormEventHandler<HTMLFormElement> =
		useCallback(
			(e) => {
				e.preventDefault();
				setShowErrors(true);
				const validationResponse = validation(formData, "all");
				if (validationResponse.errors.length) {
					setErrorUpdateUser(
						validationResponse.errors
							.map((error) => error.message)
							.join("\n * "),
					);
					return;
				} else {
					appDispatch(
						updateUser({
							name: formData.name,
							email: formData.email,
							phone: formData.phone,
							document: formData.document,
							profileImageKey: formData.profileImageKey,
						}),
					)
						.unwrap()
						.then(() => {
							appDispatch(
								commonActions[UPDATE_LOGGED_USER_ATTRIBUTES]({
									profileImageKey: formData.profileImageKey,
								}),
							);
							setSuccessUpdateUser("Atualização de usuário");
						})
						.catch(() => {
							setErrorUpdateUser("Erro ao atualizar usuário, tente novamente");
						});
				}
			},
			[formData, validation, updateUser],
		);

	const handleOnSubmitUpdatePassword: React.FormEventHandler<HTMLFormElement> =
		useCallback(
			(e) => {
				e.preventDefault();
				setShowErrors(true);
				const validationResponse = validation(formDataPassword, "all");
				if (validationResponse.errors.length) {
					setErrorUpdatePassword(
						validationResponse.errors
							.map((error) => error.message)
							.join("\n * "),
					);
					return;
				} else {
					appDispatch(updatePassword(formDataPassword))
						.unwrap()
						.then(() => {
							setSuccessUpdatePassword("Atualização de senha");
						})
						.catch((error: any) => {
							if (error.code === "#NotAuthorizedException") {
								setErrorUpdatePassword("Senha atual inválida!");
							} else if (error.code === "#LimitExceededException") {
								setErrorUpdatePassword("Bloqueio temporário na atualização");
							} else {
								setErrorUpdatePassword("Verifique os campos e tente novamente");
							}
						});
				}
			},
			[formDataPassword, validation],
		);

	// const handleSelectImage = useCallback(async (image: File) => {
	// 	try {
	// 		const imageKey = uuidv4()
	// 		const response = await appDispatch(uploadProfileImage({ imageKey, file: image })).unwrap()
	// 		setFormData({
	// 			...formData,
	// 			profileImageKey: imageKey
	// 		})
	// 		return response
	// 	} catch {
	// 		return;
	// 	}
	// }, [formData])

	if (!currentUser) {
		return <></>;
	}
	return (
		<main className="space-y-0">
			<div className="max-w-[50rem]">
				{isAdmin && (
					<form
						className="flex flex-wrap gap-10 pb-10"
						onSubmit={(e) => {
							e.preventDefault();
						}}
						noValidate
					>
						<label className="w-[38.5rem] border-b-[1px] border-[#706f6f]">
							Alterar usuário accessado - Usuário autenticado: {user.name}
						</label>
						<CustomAutoComplete
							containerClassName="min-w-[30rem] sm:min-w-full"
							fetchData={async (search) => {
								const response: any = await appDispatch(
									listUsers({ limit: 60, lastIndex: null, search }),
								).unwrap();
								return response.results;
							}}
							value={currentUser}
							onSelect={(data) => {
								if (data && data.username) {
									appDispatch(readOtherUser(data.username));
								}
							}}
							errors={[]}
							showerrors={true}
							dataDisplay={(data) => (data && data.name) || ""}
							labelNoResult="Nenhum resultado encontrado"
						/>
					</form>
				)}
				<span hidden={user.username !== currentUser.username}>
					<div className="relative w-full h-4 min-w-[60vw]">
						<Alert
							onClose={() => {
								setSuccessUpdateUser("");
							}}
							message={successUpdateUser}
							duration={5000}
							position="top-right"
							type={"success"}
						/>
						<Alert
							onClose={() => {
								setErrorUpdateUser("");
							}}
							message={errorUpdateUser}
							duration={5000}
							position="top-right"
							type={"error"}
						/>
					</div>
					<form
						className="flex flex-wrap gap-10 sm:px-2 pb-10"
						onSubmit={handleOnSubmitUpdateAttributes}
						noValidate
					>
						<label className="w-[38.5rem] border-b-[1px] border-[#706f6f] sm:min-w-full">
							Atualização de informações da conta
						</label>
						<FormField
							id="name"
							name="name"
							type="text"
							required
							onChange={(e: any) =>
								setFormData({ ...formData, name: e.target.value })
							}
							label="Nome"
							labelclassname="block text-sm font-medium leading-6 text-gray-900"
							errorclassname="text-sm text-red-500"
							containerClassName="min-w-[30rem] sm:min-w-full"
							errors={validation(formData, "name").errors}
							showErrors={!!showErrors}
							value={formData.name}
						/>
						{/* <ImageFormField
							onFileSelect={async (image) => {
								await handleSelectImage(image)
							}}
							imageKey={formData.profileImageKey}
							readFileRequest={async (imageKey) => appDispatch(readProfileImage(imageKey)).unwrap()}
							labelText={"Foto de perfil"}
							buttonText={"Mudar"}
						/> */}
						<FormField
							id="email"
							name="email"
							type="text"
							required
							onChange={(e: any) =>
								setFormData({ ...formData, email: e.target.value })
							}
							label="Email"
							labelclassname="block text-sm font-medium leading-6 text-gray-900"
							errorclassname="text-sm text-red-500"
							containerClassName="min-w-[30rem] sm:min-w-full"
							errors={validation(formData, "email").errors}
							showErrors={!!showErrors}
							value={formData.email}
						/>
						<FormField
							id="phone"
							name="phone"
							type="text"
							required
							onChange={(e: any) =>
								setFormData({
									...formData,
									phone:
										"+55" +
										unmaskNumber(e.target.value.replace("+55", "")).slice(
											0,
											11,
										),
								})
							}
							label="Celular (Whatsapp)"
							labelclassname="block text-sm font-medium leading-6 text-gray-900"
							errorclassname="text-sm text-red-500"
							containerClassName="min-w-[18rem] sm:min-w-full"
							errors={validation(formData, "phone").errors}
							showErrors={!!showErrors}
							value={maskPhone(formData.phone.replace("+55", ""))}
						/>
						<FormField
							id="document"
							name="document"
							type="text"
							required
							onChange={(e: any) =>
								setFormData({
									...formData,
									document: unmaskNumber(e.target.value).slice(0, 16),
								})
							}
							label="CPF/CNPJ"
							labelclassname="block text-sm font-medium leading-6 text-gray-900"
							errorclassname="text-sm text-red-500"
							containerClassName="min-w-[18rem] sm:min-w-full"
							errors={validation(formData, "document").errors}
							showErrors={!!showErrors}
							value={maskCpfCNPJ(formData.document)}
						/>
						<div className="min-w-[38.5rem] sm:min-w-full">
							<LoadingButton
								type="submit"
								className="flex w-fulljustify-center rounded-md bg-[#4E2581] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-[#b691e2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4E2581] group-invalid:pointer-events-none group-invalid:opacity-30"
								isloading={updateLoading}
							>
								Atualizar dados
							</LoadingButton>
						</div>
					</form>
				</span>
				<span hidden={user.username !== currentUser.username}>
					<div className="relative w-full h-10 min-w-[60vw]">
						<Alert
							onClose={() => {
								setSuccessUpdatePassword("");
							}}
							message={successUpdatePassword}
							duration={5000}
							position="top-right"
							type={"success"}
						/>
						<Alert
							onClose={() => {
								setErrorUpdatePassword("");
							}}
							message={errorUpdatePassword}
							duration={5000}
							position="top-right"
							type={"error"}
						/>
					</div>
					<form
						className="flex mt-10 flex-wrap gap-10 pb-10"
						onSubmit={handleOnSubmitUpdatePassword}
						noValidate
					>
						<label className="w-[38.5rem] border-b-[1px] border-[#706f6f]">
							Atualização de senha
						</label>
						<FormField
							id="currentPassword"
							name="currentPassword"
							type="password"
							required
							onChange={(e: any) =>
								setFormDataPassword({
									...formDataPassword,
									currentPassword: e.target.value,
								})
							}
							label="Senha atual"
							labelclassname="block text-sm font-medium leading-6 text-gray-900"
							errorclassname="text-sm text-red-500"
							containerClassName="min-w-[18rem] sm:min-w-full"
							errors={validation(formDataPassword, "currentPassword").errors}
							showErrors={!!showErrors}
						/>
						<FormField
							id="newPassword"
							name="newPassword"
							type="password"
							required
							onChange={(e: any) =>
								setFormDataPassword({
									...formDataPassword,
									newPassword: e.target.value,
								})
							}
							label="Nova senha"
							labelclassname="block text-sm font-medium leading-6 text-gray-900"
							errorclassname="text-sm text-red-500"
							containerClassName="min-w-[18rem] sm:min-w-full"
							errors={validation(formDataPassword, "newPassword").errors}
							showErrors={!!showErrors}
						/>
						<div className="min-w-[38.5rem] sm:min-w-full">
							<LoadingButton
								type="submit"
								className="flex w-fulljustify-center rounded-md bg-[#4E2581] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-[#b691e2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4E2581] group-invalid:pointer-events-none group-invalid:opacity-30"
								isloading={updatePasswordLoading}
							>
								Atualizar senha
							</LoadingButton>
						</div>
					</form>
				</span>
			</div>
		</main>
	);
}

export default Profile;
